import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackNavigation } from '../utils/analytics';
import useScrollPosition from '../hooks/useScrollPosition';

const HeaderContainer = styled(motion.header)`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
  color: white;
  padding: ${props => props.$isScrolled ? '0.5rem 0' : '1rem 0'};
  box-shadow: ${props => props.$isScrolled ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.1)'};
  position: ${props => props.$isScrolled ? 'fixed' : 'relative'};
  top: ${props => props.$isScrolled ? '0' : 'auto'};
  left: ${props => props.$isScrolled ? '0' : 'auto'};
  right: ${props => props.$isScrolled ? '0' : 'auto'};
  width: ${props => props.$isScrolled ? '100vw' : '100%'};
  max-width: ${props => props.$isScrolled ? '100vw' : 'none'};
  margin: ${props => props.$isScrolled ? '0' : '0 auto'};
  z-index: 1000;
  overflow-x: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: ${props => props.$isScrolled ? 'blur(10px)' : 'none'};
  background-opacity: ${props => props.$isScrolled ? '0.95' : '1'};
  transform-origin: top center;

  /* Ensure full viewport width when fixed - break out of any container constraints */
  ${props => props.$isScrolled && `
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    width: 100vw !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    transform: translateX(0) !important;
    box-sizing: border-box;
  `}
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  
  @media (max-width: 1100px) {
    flex-wrap: wrap;
  }
`;

const Logo = styled(Link)`
  color: white;
  font-size: ${props => props.$isScrolled ? '1.4rem' : '1.8rem'};
  font-weight: bold;
  text-decoration: none;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    text-decoration: none;
    opacity: 0.9;
    transform: scale(1.02);
  }

  @media (max-width: 1100px) {
    font-size: ${props => props.$isScrolled ? '1.3rem' : '1.6rem'};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.$isScrolled ? '1.2rem' : '1.4rem'};
  }
`;

const Heart = styled.span`
  color: #e53e3e;
  margin-right: 0.5rem;
  font-size: ${props => props.$isScrolled ? '1.5rem' : '2rem'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.$isScrolled ? '1.2rem' : '1.6rem'};
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 1.5rem;
  flex-shrink: 0;
  
  @media (max-width: 1100px) {
    width: 100%;
    margin-top: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 0.8rem;
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: ${props => props.$isScrolled ? '0.3rem 0.6rem' : '0.5rem 0.8rem'};
  border-radius: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  font-size: ${props => props.$isScrolled ? '0.9rem' : '1rem'};
  background-color: ${props => props.$active ? 'rgba(255,255,255,0.2)' : 'transparent'};
  white-space: nowrap;

  &:hover {
    background-color: rgba(255,255,255,0.2);
    text-decoration: none;
    transform: translateY(-1px);
  }

  @media (max-width: 1100px) {
    padding: ${props => props.$isScrolled ? '0.3rem 0.5rem' : '0.4rem 0.7rem'};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.$isScrolled ? '0.25rem 0.5rem' : '0.3rem 0.6rem'};
    font-size: ${props => props.$isScrolled ? '0.85rem' : '0.9rem'};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: block;
  }
`;

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isScrolled } = useScrollPosition();

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/gallery', label: 'Heroes & Journey' },
    { path: '/spiritual', label: 'God\'s Grace' },
    { path: '/gratitude', label: 'Gratitude' },
    { path: '/elizas-award', label: 'Eliza' },
    { path: '/timeline', label: 'Timeline' }
  ];

  // Smooth scroll to top function with optimized timing
  const scrollToTop = () => {
    // Use requestAnimationFrame for better coordination with browser rendering
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  };

  return (
    <HeaderContainer
      $isScrolled={isScrolled}
      initial={{ opacity: 1 }}
      animate={{
        opacity: 1
      }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      <HeaderContent>
        <Logo
          to="/"
          $isScrolled={isScrolled}
          onClick={scrollToTop}
        >
          <Heart $isScrolled={isScrolled}>❤️</Heart>
          Maggie's Miracle
        </Logo>

        <MobileMenuButton
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
          $isScrolled={isScrolled}
        >
          ☰
        </MobileMenuButton>

        <Nav style={{ display: mobileMenuOpen ? 'flex' : 'flex' }}>
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              $active={location.pathname === item.path}
              $isScrolled={isScrolled}
              onClick={() => {
                setMobileMenuOpen(false);
                trackNavigation(item.label);
                // Scroll to top when clicking navigation links - optimized timing
                scrollToTop();
              }}
            >
              {item.label}
            </NavLink>
          ))}
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
